import { create } from 'zustand';

export const useSystemLogsStore = create((set) => ({
    // ── Tab State ───────────────────────────────────────────────
    activeTab: 'explorer',
    setActiveTab: (tab) => set({ activeTab: tab }),

    // ── Explorer (Log List) ─────────────────────────────────────
    items: [],
    loading: false,
    error: null,
    searchParams: {
        page: 1,
        pageSize: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: '',
        action: '',
        actionMode: 'contains',
        resource: '',
        resourceId: '',
        user: '',
        status: '',
        severity: '',
        category: '',
        dateFrom: '',
        dateTo: '',
        ip: '',
        sessionId: '',
        requestId: '',
        actionFamilies: ''
    },
    paginationData: { total: 0, totalPages: 0, page: 1, pageSize: 20 },
    uniqueValues: { actions: [], resources: [], categories: [], severities: [] },

    setItems: (items) => set({ items }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),
    setSearchParams: (params) => set((s) => ({
        searchParams: { ...s.searchParams, ...params }
    })),
    setPaginationData: (paginationData) => set({ paginationData }),
    setUniqueValues: (uniqueValues) => set({ uniqueValues }),

    // Selection is keyed by tenant so IDs can never carry across tenant scopes.
    selectedLogIdsByTenant: {},
    setSelectedLogIds: (tenantId, ids) => set((state) => ({
        selectedLogIdsByTenant: {
            ...state.selectedLogIdsByTenant,
            [String(tenantId ?? '')]: [...new Set((ids || []).map(String))]
        }
    })),
    clearSelectedLogIds: (tenantId) => set((state) => {
        if (tenantId == null) return { selectedLogIdsByTenant: {} };
        const next = { ...state.selectedLogIdsByTenant };
        delete next[String(tenantId)];
        return { selectedLogIdsByTenant: next };
    }),

    // ── Selected Log Detail ─────────────────────────────────────
    selectedLog: null,
    relatedLogs: [],
    detailLoading: false,
    isDetailOpen: false,

    setSelectedLog: (log) => set({ selectedLog: log }),
    setRelatedLogs: (logs) => set({ relatedLogs: logs }),
    setDetailLoading: (loading) => set({ detailLoading: loading }),
    openDetail: (log) => set({ isDetailOpen: true, selectedLog: log }),
    closeDetail: () => set({ isDetailOpen: false, selectedLog: null, relatedLogs: [] }),

    // ── Live Stream ─────────────────────────────────────────────
    liveEntries: [],
    isStreaming: true,
    liveLoading: false,
    liveSeverityFilter: [],
    liveActionFamilies: '',

    setLiveEntries: (entries) => set({ liveEntries: entries }),
    prependLiveEntries: (newEntries) => set((s) => {
        const incoming = Array.isArray(newEntries) ? newEntries : [];
        const seen = new Set(s.liveEntries.map((e) => String(e?._id ?? '')));
        const fresh = incoming.filter((e) => e?._id && !seen.has(String(e._id)));
        return { liveEntries: [...fresh, ...s.liveEntries].slice(0, 200) };
    }),
    setIsStreaming: (streaming) => set({ isStreaming: streaming }),
    setLiveLoading: (loading) => set({ liveLoading: loading }),
    setLiveSeverityFilter: (filter) => set({ liveSeverityFilter: filter }),
    setLiveActionFamilies: (liveActionFamilies) => set({ liveActionFamilies }),

    // ── Delta / Timeline ────────────────────────────────────────
    entityTimeline: [],
    timelineLoading: false,
    timelineAppending: false,
    timelinePagination: { total: 0, totalPages: 0, page: 1, pageSize: 50 },
    selectedResourceType: '',
    selectedResourceId: '',
    deltaPickerItems: [],
    deltaPickerLoading: false,

    setEntityTimeline: (timeline) => set({ entityTimeline: timeline }),
    setTimelineLoading: (loading) => set({ timelineLoading: loading }),
    setTimelineAppending: (loading) => set({ timelineAppending: loading }),
    setTimelinePagination: (pagination) => set({ timelinePagination: pagination }),
    setSelectedResource: (resourceType, resourceId) => set({
        selectedResourceType: resourceType,
        selectedResourceId: resourceId
    }),
    setDeltaPickerItems: (items) => set({ deltaPickerItems: Array.isArray(items) ? items : [] }),
    setDeltaPickerLoading: (loading) => set({ deltaPickerLoading: loading }),

    // ── Page header stat cards (today / scoped; not overwritten by analytics fetch)
    statBar: {
        totalToday: 0,
        errorCount: 0,
        activeUsers: 0,
        criticalActions: 0,
        loading: false
    },
    setStatBar: (partial) => set((s) => ({ statBar: { ...s.statBar, ...partial } })),

    // ── Analytics ───────────────────────────────────────────────
    stats: {
        totalLogs: 0,
        errorRate: 0,
        actionCounts: [],
        topUsers: [],
        hourlyDistribution: [],
        severityCounts: [],
        categoryCounts: [],
        statusCounts: []
    },
    heatmapData: [],
    statsLoading: false,
    heatmapLoading: false,

    setStats: (stats) => set({ stats }),
    setHeatmapData: (data) => set({ heatmapData: data }),
    setStatsLoading: (loading) => set({ statsLoading: loading }),
    setHeatmapLoading: (loading) => set({ heatmapLoading: loading }),

    // ── Security ────────────────────────────────────────────────
    securitySummary: {
        failedLogins: { last24h: 0, last7d: 0, last30d: 0 },
        accessDenials: { last24h: 0, last7d: 0 },
        criticalActions: [],
        anomalies: { score: 0, bruteForceAttempts: [], bulkDeletes: [] }
    },
    securityLoading: false,

    setSecuritySummary: (summary) => set({ securitySummary: summary }),
    setSecurityLoading: (loading) => set({ securityLoading: loading }),

    // ── Compliance data retention ───────────────────────────────
    retentionInfo: null,
    setRetentionInfo: (info) => set({ retentionInfo: info }),

    // ── Export ───────────────────────────────────────────────────
    exportLoading: false,
    setExportLoading: (loading) => set({ exportLoading: loading }),

    // ── HTTP access logs ─────────────────────────────────────────
    accessLogItems: [],
    accessLogLoading: false,
    accessLogPagination: { total: 0, totalPages: 0, page: 1, pageSize: 20 },
        accessLogParams: {
        page: 1,
        pageSize: 20,
        search: '',
        method: '',
        statusCode: '',
        statusClass: '',
        pathPrefix: '',
        userId: '',
        dateFrom: '',
        dateTo: ''
    },

    setAccessLogItems: (items) => set({ accessLogItems: Array.isArray(items) ? items : [] }),
    setAccessLogsLoading: (loading) => set({ accessLogLoading: loading }),
    setAccessLogPagination: (pagination) => set({ accessLogPagination: pagination }),
    setAccessLogParams: (params) => set((s) => ({
        accessLogParams: { ...s.accessLogParams, ...params }
    })),

    // ── Reset ───────────────────────────────────────────────────
    resetTenantData: () => set((state) => ({
        items: [],
        loading: false,
        exportLoading: false,
        error: null,
        selectedLogIdsByTenant: {},
        paginationData: { total: 0, totalPages: 0, page: 1, pageSize: state.searchParams.pageSize },
        uniqueValues: { actions: [], resources: [], categories: [], severities: [] },
        selectedLog: null,
        relatedLogs: [],
        detailLoading: false,
        isDetailOpen: false,
        liveEntries: [],
        liveLoading: false,
        entityTimeline: [],
        timelineLoading: false,
        timelineAppending: false,
        deltaPickerItems: [],
        deltaPickerLoading: false,
        statBar: { totalToday: 0, errorCount: 0, activeUsers: 0, criticalActions: 0, loading: true },
        stats: { totalLogs: 0, errorRate: 0, actionCounts: [], topUsers: [], hourlyDistribution: [], severityCounts: [], categoryCounts: [], statusCounts: [] },
        heatmapData: [],
        statsLoading: false,
        heatmapLoading: false,
        securitySummary: { failedLogins: { last24h: 0, last7d: 0, last30d: 0 }, accessDenials: { last24h: 0, last7d: 0 }, criticalActions: [], anomalies: { score: 0, bruteForceAttempts: [], bulkDeletes: [] } },
        securityLoading: false,
        retentionInfo: null,
        accessLogItems: [],
        accessLogLoading: false,
        accessLogPagination: { total: 0, totalPages: 0, page: 1, pageSize: state.accessLogParams.pageSize },
        searchParams: { ...state.searchParams, page: 1, search: '', action: '', resource: '', resourceId: '', user: '', status: '', severity: '', category: '', dateFrom: '', dateTo: '', ip: '', sessionId: '', requestId: '', actionFamilies: '' },
        accessLogParams: { ...state.accessLogParams, page: 1, search: '', method: '', statusCode: '', statusClass: '', pathPrefix: '', userId: '', dateFrom: '', dateTo: '' }
    })),
    resetFilters: () => set({
        selectedLogIdsByTenant: {},
        searchParams: {
            page: 1, pageSize: 20, sortBy: 'createdAt', sortOrder: 'desc',
            search: '', action: '', actionMode: 'contains', resource: '', resourceId: '',
            user: '', status: '', severity: '', category: '', dateFrom: '', dateTo: '',
            ip: '', sessionId: '', requestId: '',
            actionFamilies: ''
        }
    })
}));
